import React, { useMemo } from "react";
import { DataGrid, GridRowModes, GridRowEditStopReasons, GridToolbarContainer, useGridApiRef } from "@mui/x-data-grid";
import { Box, IconButton, Tooltip, Button, Divider } from "@mui/material";
import { Add, Cancel, Delete, Edit, Save } from "@mui/icons-material";

import { DeleteModal } from "./DeleteModal";

/**
 * A toolbar component for CRUD tables.
 * It renders an add button with a tooltip and a divider below.
 * @param {function} onAdd - The function to call when the add button is clicked.
 * @param {string} addButtonText - The text to display in the tooltip.
 * @param {boolean} showAddButton - Whether to show the add button.
 */
function CrudToolbar({ onAdd, addButtonText }) {
  return (
    <Box>
      <GridToolbarContainer sx={{ px: 1 }}>
        <Tooltip title={addButtonText}>
          <Button
            startIcon={<Add />}
            onClick={onAdd}
            size="small"
            sx={{
              color: "#674BF5",
              textTransform: "capitalize",
              fontWeight: 500,
              "& .MuiSvgIcon-root": {
                color: "#674BF5",
              },
            }}
          >
            {addButtonText}
          </Button>
        </Tooltip>
      </GridToolbarContainer>

      {/* Divider between Add button and column headers */}
      <Divider sx={{ borderBottomWidth: 2, borderColor: "#5e5d72" }} />
    </Box>
  );
}

export const CrudTable = ({
  rows = [],
  setRows = () => {},
  columns = [],
  addButtonText = "Add",
  showAddButton = false,
  hidePagination = false,
  pageSizeOptions = [5, 10, 20],
  style = {},
  // Optional (newRow, oldRow) => finalRow. Runs synchronously so DataGrid's own optimistic
  // row cache (updated from processRowUpdate's return value, independent of the async setRows
  // state round-trip) reflects any derived-field recalculation immediately instead of one render late.
  onRowUpdate,
  // Optional — when set, deleting a row asks for confirmation (via this message) first instead of
  // removing it immediately. Omit to keep the previous immediate-delete behavior.
  confirmDeleteMessage,
  // Optional (row) => boolean. Defaults to following the same `row.disabled` flag Edit uses, but
  // a caller can override this to still allow deleting a row that's locked from editing for a
  // reason that doesn't also justify blocking its removal (e.g. a row that can't be submitted
  // this time around but should still be removable from the table).
  isDeleteDisabled = (row) => !!row?.disabled,
}) => {
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [deleteTargetId, setDeleteTargetId] = React.useState(null);
  const apiRef = useGridApiRef();

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const handleRowEditStop = (params, event) => {};

  const handleAddRow = () => {
    const id = Date.now();
    setRows((prev) => [
      {
        id,
        isNew: true,
      },
      ...prev,
    ]);
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  const processRowUpdate = (newRow, oldRow) => {
    const updatedRow = onRowUpdate ? onRowUpdate({ ...newRow, isNew: false }, oldRow) : { ...newRow, isNew: false };
    setRows((prev) => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const withSerialNumber = (columns) => [
    {
      field: "__slno__",
      headerName: "Sl No",
      flex: 0.5,
      sortable: false,
      editable: false,
      renderCell: (params) => {
        const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return index + 1;
      },
    },
    ...columns,
  ];

  const actionsColumn = {
    field: "__actions__",
    type: "actions",
    headerName: "Actions",
    flex: 1,
    sortable: false,
    getActions: ({ id, row }) => {
      const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isEditing) {
        return [
          <IconButton
            size="small"
            onClick={() =>
              setRowModesModel((prev) => ({
                ...prev,
                [id]: { mode: GridRowModes.View },
              }))
            }
          >
            <Save fontSize="small" />
          </IconButton>,
          <IconButton
            size="small"
            onClick={() =>
              setRowModesModel((prev) => ({
                ...prev,
                [id]: {
                  mode: GridRowModes.View,
                  ignoreModifications: true,
                },
              }))
            }
          >
            <Cancel fontSize="small" />
          </IconButton>,
        ];
      }

      return [
        <Tooltip title={row?.disabled ? row?.disabledReason || "" : ""}>
          <span>
            <IconButton
              size="small"
              disabled={!!row?.disabled}
              onClick={() =>
                setRowModesModel((prev) => ({
                  ...prev,
                  [id]: { mode: GridRowModes.Edit },
                }))
              }
            >
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>,
        <Tooltip title={isDeleteDisabled(row) ? row?.disabledReason || "" : ""}>
          <span>
            <IconButton
              size="small"
              disabled={isDeleteDisabled(row)}
              onClick={() => (confirmDeleteMessage ? setDeleteTargetId(id) : removeRow(id))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>,
      ];
    },
  };

  const enhancedColumns = useMemo(() => {
    return [...withSerialNumber(columns.map((col) => ({ editable: true, ...col }))), actionsColumn];
  }, [columns, rowModesModel]);

  const handleCellClick = (params) => {
    const { id, field, isEditable, row } = params;
    if (!isEditable || row?.disabled) return;
    if (rowModesModel[id]?.mode === GridRowModes.Edit) return;
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
    requestAnimationFrame(() => {
      try {
        apiRef.current.setCellFocus(id, field);
      } catch (_) {}
    });
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={enhancedColumns}
        apiRef={apiRef}
        onCellClick={handleCellClick}
        isCellEditable={(params) => !params.row?.disabled}
        getRowClassName={(params) => (params.row?.disabled ? "crud-row-disabled" : "")}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        pageSizeOptions={pageSizeOptions}
        disableColumnMenu
        disableColumnSorting
        hideFooterPagination={hidePagination}
        slots={{
          toolbar: showAddButton ? CrudToolbar : null,
        }}
        slotProps={{
          toolbar: {
            onAdd: handleAddRow,
            addButtonText,
          },
        }}
        sx={{
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell": {
            color: "#5e5d72",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            textTransform: "capitalize",
            color: "#5e5d72",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: hidePagination ? "1px" : "auto",
            height: hidePagination ? "1px" : "auto",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            backgroundColor: "#fff",
            overflow: "hidden",
          },
          "& .crud-row-disabled": {
            boxShadow: "inset 3px 0 0 0 #d32f2f",
            backgroundColor: "rgba(211, 47, 47, 0.06)",
          },
          "& .crud-row-disabled:hover": {
            backgroundColor: "rgba(211, 47, 47, 0.1)",
          },
          "& .crud-row-disabled .MuiDataGrid-cell": {
            color: "#8a8a8a",
          },
          ...style,
        }}
      />

      {!!confirmDeleteMessage && (
        <DeleteModal
          isOpen={deleteTargetId != null}
          onClose={() => setDeleteTargetId(null)}
          onConfirm={() => {
            removeRow(deleteTargetId);
            setDeleteTargetId(null);
          }}
          message={confirmDeleteMessage}
        />
      )}
    </Box>
  );
};
